import React, { useEffect, useMemo, useState } from "react";
import AppModal from "../AppModal";
import {
	CONHECIMENTO_CATEGORIES,
	normalizeConhecimentoCategory,
} from "../../pages/OpenAi/conhecimentoCategories";

function buildTransferContent(rules) {
	const valid = (Array.isArray(rules) ? rules : []).filter(
		(r) => String(r.when || "").trim() && String(r.sectorName || "").trim()
	);
	if (!valid.length) return "";

	const lines = [
		"Regras de transferência do agente.",
		"Use SEMPRE o nome EXATO do setor na tag [TRANSFER]nome[/TRANSFER].",
		"Não invente setores fora desta lista.",
		"",
	];

	valid.forEach((rule, index) => {
		const when = String(rule.when).trim();
		const sector = String(rule.sectorName).trim();
		lines.push(`#RULE`);
		lines.push(`WHEN: ${when}`);
		lines.push(`SECTOR: ${sector}`);
		lines.push(`#END`);
		lines.push("");
		lines.push(`${index + 1}) Quando: ${when}`);
		lines.push(`   Transferir para: ${sector}`);
		lines.push(`   Tag obrigatória: [TRANSFER]${sector}[/TRANSFER]`);
		lines.push("");
	});

	return lines.join("\n").trim();
}

function parseTransferRules(content) {
	const text = String(content || "");
	const blocks = text.match(/#RULE[\s\S]*?#END/gi) || [];
	if (!blocks.length) return [];

	return blocks
		.map((block) => {
			const whenMatch = block.match(/WHEN:\s*(.+)/i);
			const sectorMatch = block.match(/SECTOR:\s*(.+)/i);
			const when = whenMatch ? whenMatch[1].trim() : "";
			const sectorName = sectorMatch ? sectorMatch[1].trim() : "";
			if (!when || !sectorName) return null;
			return { id: `${Date.now()}-${Math.random()}`, when, sectorName };
		})
		.filter(Boolean);
}

function emptyRule() {
	return { id: `${Date.now()}-${Math.random()}`, when: "", sectorName: "" };
}

export default function ConhecimentoModal({
	open,
	handleClose,
	handleSubmit,
	title,
	setTitle,
	content,
	setContent,
	category,
	setCategory,
	loading,
	titleHeader,
	transferQueues = [],
}) {
	const [transferRules, setTransferRules] = useState([emptyRule()]);

	const isTransfer = normalizeConhecimentoCategory(category) === "transferencia";

	const queueOptions = useMemo(() => {
		const list = Array.isArray(transferQueues) ? transferQueues : [];
		return list
			.filter((q) => q && q.id != null && String(q.name || "").trim())
			.map((q) => ({
				id: Number(q.id),
				name: String(q.name).trim(),
				color: q.color || "#6366f1",
			}));
	}, [transferQueues]);

	useEffect(() => {
		if (!open || !isTransfer) return;
		const parsed = parseTransferRules(content);
		setTransferRules(parsed.length ? parsed : [emptyRule()]);
		// Só ao abrir o modal / mudar para a categoria — evita loop com o rebuild do content.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, isTransfer]);

	useEffect(() => {
		if (!open || !isTransfer) return;
		setContent(buildTransferContent(transferRules));
	}, [transferRules, isTransfer, open, setContent]);

	if (!open) return null;

	const updateRule = (id, patch) => {
		setTransferRules((prev) =>
			prev.map((rule) => (rule.id === id ? { ...rule, ...patch } : rule))
		);
	};

	const addRule = () => {
		setTransferRules((prev) => [...prev, emptyRule()]);
	};

	const removeRule = (id) => {
		setTransferRules((prev) => {
			const next = prev.filter((rule) => rule.id !== id);
			return next.length ? next : [emptyRule()];
		});
	};

	const onSubmit = (e) => {
		e.preventDefault();
		if (isTransfer) {
			const built = buildTransferContent(transferRules);
			if (!built) return;
			setContent(built);
			if (!String(title || "").trim()) {
				setTitle("Regras de transferência");
			}
		}
		handleSubmit(e);
	};

	return (
		<AppModal
			open={open}
			onClose={handleClose}
			title={titleHeader}
			subtitle="Cadastre informações estruturadas que ajudam a IA a responder melhor."
			maxWidth={720}
		>
			<form onSubmit={onSubmit} className="space-y-4">
				<div className="rounded-2xl border border-primary/10 bg-primary/5 p-4 dark:border-primary/20 dark:bg-primary/10">
					<p className="m-0 text-sm leading-6 text-default-600 dark:text-default-300">
						{isTransfer
							? "Escolha o setor na lista (nome exato). Assim a IA não erra o nome na transferência."
							: "Use um título claro, escolha a categoria certa e descreva o conteúdo de forma objetiva."}
					</p>
				</div>

				<div className="space-y-2">
					<label
						htmlFor="conhecimento-modal-title"
						className="block text-sm font-medium text-default-700 dark:text-default-200"
					>
						Título
					</label>
					<input
						id="conhecimento-modal-title"
						type="text"
						required
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						className="form-input w-full"
						placeholder={
							isTransfer
								? "Ex.: Regras de transferência"
								: "Ex.: Política de cancelamento"
						}
					/>
				</div>

				<div className="space-y-2">
					<label
						htmlFor="conhecimento-modal-category"
						className="block text-sm font-medium text-default-700 dark:text-default-200"
					>
						Categoria
					</label>
					<select
						id="conhecimento-modal-category"
						required
						value={normalizeConhecimentoCategory(category)}
						onChange={(e) => setCategory(e.target.value)}
						className="form-input w-full"
					>
						{CONHECIMENTO_CATEGORIES.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
				</div>

				{isTransfer ? (
					<div className="space-y-3">
						<div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 p-4 dark:border-amber-400/20 dark:bg-amber-400/10">
							<p className="m-0 text-sm font-medium text-default-800 dark:text-default-100">
								Tag usada pela IA
							</p>
							<code className="mt-2 block rounded-lg bg-white/70 px-3 py-2 text-xs text-default-700 dark:bg-default-950/40 dark:text-default-200">
								[TRANSFER]Nome do Setor[/TRANSFER]
							</code>
							<p className="mt-2 mb-0 text-xs leading-5 text-default-600 dark:text-default-300">
								O setor é preenchido automaticamente pelo select abaixo.
							</p>
						</div>

						{queueOptions.length === 0 ? (
							<div className="rounded-2xl border border-dashed border-default-300 px-4 py-6 text-center dark:border-default-600">
								<p className="m-0 text-sm font-medium text-default-800 dark:text-default-100">
									Nenhum setor de transferência liberado
								</p>
								<p className="mt-1 mb-0 text-xs text-default-500 dark:text-default-400">
									Edite o agente e marque os setores em “Setores para transferência”.
								</p>
							</div>
						) : (
							<div className="space-y-3">
								{transferRules.map((rule, index) => (
									<div
										key={rule.id}
										className="rounded-2xl border border-default-200 bg-default-50/80 p-4 dark:border-default-700 dark:bg-default-900/50"
									>
										<div className="mb-3 flex items-center justify-between gap-2">
											<p className="m-0 text-xs font-semibold uppercase tracking-wide text-default-400">
												Regra {index + 1}
											</p>
											{transferRules.length > 1 ? (
												<button
													type="button"
													onClick={() => removeRule(rule.id)}
													className="text-xs font-medium text-danger hover:underline"
												>
													Remover
												</button>
											) : null}
										</div>

										<div className="grid grid-cols-1 gap-3 sm:grid-cols-[1.4fr_1fr]">
											<label className="block">
												<span className="mb-1.5 block text-xs font-medium text-default-500">
													Quando transferir
												</span>
												<input
													type="text"
													required
													className="form-input w-full"
													placeholder="Ex.: cliente pedir orçamento"
													value={rule.when}
													onChange={(e) =>
														updateRule(rule.id, { when: e.target.value })
													}
												/>
											</label>

											<label className="block">
												<span className="mb-1.5 block text-xs font-medium text-default-500">
													Setor destino
												</span>
												<select
													required
													className="form-input w-full"
													value={rule.sectorName}
													onChange={(e) =>
														updateRule(rule.id, { sectorName: e.target.value })
													}
												>
													<option value="">Selecione o setor</option>
													{queueOptions.map((q) => (
														<option key={q.id} value={q.name}>
															{q.name}
														</option>
													))}
												</select>
											</label>
										</div>

										{rule.sectorName ? (
											<p className="mt-2 mb-0 text-[11px] text-default-400">
												Tag gerada:{" "}
												<code className="text-primary">
													[TRANSFER]{rule.sectorName}[/TRANSFER]
												</code>
											</p>
										) : null}
									</div>
								))}

								<button
									type="button"
									onClick={addRule}
									className="btn btn-sm w-full border border-dashed border-default-300 bg-transparent text-default-600 hover:border-primary/30 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
								>
									+ Adicionar regra
								</button>
							</div>
						)}
					</div>
				) : (
					<div className="space-y-2">
						<label
							htmlFor="conhecimento-modal-content"
							className="block text-sm font-medium text-default-700 dark:text-default-200"
						>
							Conteúdo
						</label>
						<textarea
							id="conhecimento-modal-content"
							required
							rows={10}
							value={content}
							onChange={(e) => setContent(e.target.value)}
							className="form-input min-h-[220px] w-full"
							placeholder="Descreva o conhecimento de forma clara e objetiva..."
						/>
					</div>
				)}

				<div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
					<button
						type="button"
						className="btn btn-sm border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
						onClick={handleClose}
					>
						Cancelar
					</button>
					<button
						type="submit"
						disabled={
							loading ||
							(isTransfer &&
								(queueOptions.length === 0 ||
									!transferRules.some(
										(r) => r.when.trim() && r.sectorName.trim()
									)))
						}
						className="btn btn-sm bg-primary text-white disabled:opacity-60"
					>
						{loading ? "Salvando..." : "Salvar"}
					</button>
				</div>
			</form>
		</AppModal>
	);
}
